import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useGlobalContext } from '../../../../Context/GlobalContext';
import { getTherapistList, saveTherapistSortOrder } from '../../../../APICalling/APICalling';
import SearchIcon from '../../../../Components/Icons/SearchIcon';
import RepeatBookingIcon from '../../../../Components/Icons/RepeatBookingIcon';
import Echo from '../../../../Components/Sockect/Echo';
import styles from './TherapistList.module.css';

const getConsistentColor = (gender) => {
    return gender?.toLowerCase() === 'female' ? 'female' : 'male';
};


let globalTherapistCache = {};

const TherapistList = ({ onSelectTherapist, refreshTrigger, activeTherapistId, forceShowArchived = false, onClearSelection }) => {
    const [searchParams] = useSearchParams();
    const { selectedOutlet, loggedUser, userAssignedOutlets, setHomeStaffList } = useGlobalContext();
    
    // Initialize state from global cache for the selected outlet
    const getInitialData = () => {
        if (!selectedOutlet) return [];
        const cachedData = globalTherapistCache[selectedOutlet]?.data;
        
        // Handle legacy cache (array)
        if (Array.isArray(cachedData)) {
            return cachedData;
        }

        if (cachedData?.success) {
             const staffList = cachedData?.data?.list?.staffs || [];
             return staffList.filter((staff) => {
                const role = (staff.role || '').toString().toLowerCase();
                const isReception = role.includes('receptionist');
                const isStaff = role ? role.includes('staff') : true;
                return !isReception && isStaff;
            });
        }
        return [];
    };

    const [therapists, setTherapists] = useState(getInitialData);
    const [filteredTherapists, setFilteredTherapists] = useState(getInitialData);
    const [archivedTherapists, setArchivedTherapists] = useState([]);
    const [filteredArchivedTherapists, setFilteredArchivedTherapists] = useState([]);
    const [searchQuery, setSearchQuery] = useState('');
    const [showArchived, setShowArchived] = useState(false);
    // Initialize loading based on whether we have data for this outlet
    const [isLoading, setIsLoading] = useState(() => {
        if (!selectedOutlet) return false;
        return !globalTherapistCache[selectedOutlet]?.data;
    });
    const [isLoadingArchived, setIsLoadingArchived] = useState(false);
    const [selectedTherapistId, setSelectedTherapistId] = useState(null);
    const therapistListRef = useRef(null);
    const skipNextAutoScrollRef = useRef(false);
    const suppressForceShowArchivedRef = useRef(false);
    const [draggedTherapistId, setDraggedTherapistId] = useState(null);
    const [dropTargetTherapistId, setDropTargetTherapistId] = useState(null);
    const [dropIndicator, setDropIndicator] = useState({ id: null, position: 'before' });
    const dragImageRef = useRef(null);
    const [customDragPreview, setCustomDragPreview] = useState(null);
    const dragStartPos = useRef({ x: 0, y: 0 });
    const isDraggingRef = useRef(false);
    const hasMovedRef = useRef(false);
    const therapistsOrderRef = useRef([]);
    const [hasDataUpdate, setHasDataUpdate] = useState(false);
    const [localRefreshTrigger, setLocalRefreshTrigger] = useState(0);
    const lastFetchTimeRef = useRef(0);

    // Force-clear cache on parent refresh trigger (archive/unarchive, etc.)
    useEffect(() => {
        if (!selectedOutlet) return;
        if (globalTherapistCache[selectedOutlet]) {
            delete globalTherapistCache[selectedOutlet];
        }
        lastFetchTimeRef.current = 0;
    }, [refreshTrigger, selectedOutlet]);

    // Generate cache key based on outlet and status
    const getCacheKey = (outletId, status) => `therapists_${outletId}_${status}`;
    const getETagKey = (outletId, status) => `therapists_etag_${outletId}_${status}`;

    // Update state when selectedOutlet changes (if we have cached data for the new outlet)
    useEffect(() => {
        if (selectedOutlet) {
            const cachedData = globalTherapistCache[selectedOutlet]?.data;
            
            if (Array.isArray(cachedData)) {
                setTherapists(cachedData);
                setFilteredTherapists(cachedData);
                setIsLoading(false);
                return;
            }

            if (cachedData?.success) {
                const staffList = cachedData?.data?.list?.staffs || [];
                const therapistsOnly = staffList.filter((staff) => {
                   const role = (staff.role || '').toString().toLowerCase();
                   const isReception = role.includes('receptionist');
                   const isStaff = role ? role.includes('staff') : true;
                   return !isReception && isStaff;
               });
                setTherapists(therapistsOnly);
                setFilteredTherapists(therapistsOnly);
                setIsLoading(false);
            } else {
                 setTherapists([]);
                 setFilteredTherapists([]);
                 setIsLoading(true);
            }
        }
    }, [selectedOutlet]);

    // Fetch active therapists data with caching optimization
     useEffect(() => {
        const fetchTherapists = async () => {
            // Wait for user assigned outlets to be loaded first
            if (!userAssignedOutlets || userAssignedOutlets.length === 0) {
                return;
            }
            
            // Don't fetch if outlet is not selected
            if (!selectedOutlet) {
                return;
            }
            
            // Validate that the selected outlet exists in user's assigned outlets with edit permission
            const assignedOutlet = userAssignedOutlets.find(outlet => String(outlet.id) === String(selectedOutlet));
            if (!assignedOutlet) {
                // Clear any existing data and cache
                setTherapists([]);
                setFilteredTherapists([]);
                if (globalTherapistCache[selectedOutlet]) {
                    delete globalTherapistCache[selectedOutlet];
                }
                return;
            }
            
            // Check if user has edit permission for this outlet (required for therapist management)
            if (assignedOutlet.mode !== 'edit') {
                setTherapists([]);
                setFilteredTherapists([]);
                 if (globalTherapistCache[selectedOutlet]) {
                    delete globalTherapistCache[selectedOutlet];
                }
                return;
            }

            // Throttle protection: prevent calling API more than once every 500ms
            const now = Date.now();
            if (now - lastFetchTimeRef.current < 500) {
                return;
            }
            lastFetchTimeRef.current = now;

            setIsLoading(true);
            
            // Only show full loading spinner if we don't have any data needed to display
            if (therapists.length === 0 && !globalTherapistCache[selectedOutlet]?.data) {
                setIsLoading(true);
            }

            // Use global etag if available
            let cachedEtag = globalTherapistCache[selectedOutlet]?.etag;

            try {
                // Pass true for forceFullResponse to match getClientList signature, though APICallingWithETag is always used
                const response = await getTherapistList({
                    outlet: selectedOutlet,
                    pagination: 0,
                    status: 1, // 1 for active
                    receptionist_id: loggedUser || null,
                }, cachedEtag, true);

                let responseData;

                if (response?.status === 304) {
                     // Data not modified, use cache
                     if (globalTherapistCache[selectedOutlet]?.data) {
                         responseData = globalTherapistCache[selectedOutlet].data;
                     }
                } else if (response?.data) {
                     // Standard success response (200)
                     responseData = response.data;
                     
                     // Update cache and ETag if present in headers
                     const newEtag = response.headers?.['etag'] || response.headers?.['ETag'];
                     if (newEtag) {
                         if (!globalTherapistCache[selectedOutlet]) globalTherapistCache[selectedOutlet] = {};
                         globalTherapistCache[selectedOutlet].etag = newEtag;
                     }

                     // Update global cache data (store full response)
                     if (!globalTherapistCache[selectedOutlet]) globalTherapistCache[selectedOutlet] = {};
                     globalTherapistCache[selectedOutlet].data = responseData;
                }

                // Process responseData (handle both Legacy Array and New Object formats)
                let finalTherapistList = [];
                

                if (Array.isArray(responseData)) {
                    finalTherapistList = responseData;
                } else if (responseData?.success) {
                    // Try paths: active standard path or Clients.js style nested path
                    const staffList = responseData?.data?.list?.staffs || responseData?.data?.data?.list?.staffs || [];
                    
                    //console.log('Raw staffList:', staffList);

                    finalTherapistList = staffList.filter((staff) => {
                        // Safety check for staff object
                        if (!staff) return false;

                        const role = (staff.role || '').toString().toLowerCase();
                        const isReception = role.includes('receptionist');
                        
  
                        return !isReception; 
                    });
                } else if (responseData?.data?.success) {
                    // Handle potential double-nested data structure (some API wrappers do this)
                     const staffList = responseData?.data?.data?.list?.staffs || [];
                     finalTherapistList = staffList.filter((staff) => {
                        if (!staff) return false;
                        const role = (staff.role || '').toString().toLowerCase();
                        return !role.includes('receptionist'); 
                     });
                }
                
                if (finalTherapistList.length > 0 || responseData?.success || Array.isArray(responseData) || responseData?.data?.success) {
                    setTherapists(finalTherapistList);
                    setFilteredTherapists(finalTherapistList);
                    setHasDataUpdate(false);
                } else if (!isLoading) {
                     setTherapists([]);
                     setFilteredTherapists([]);
                }

            } catch (error) {
                console.error('Error fetching therapists:', error);
                setTherapists([]);
                setFilteredTherapists([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchTherapists();
    }, [selectedOutlet, refreshTrigger, userAssignedOutlets, loggedUser, localRefreshTrigger]);

    // Update selected therapist ID from URL params
    useEffect(() => {
        const therapistIdFromUrl = searchParams.get('therapist');
        if (therapistIdFromUrl) {
            const therapistId = parseInt(therapistIdFromUrl, 10);
            if (!isNaN(therapistId)) {
                setSelectedTherapistId(therapistId);
            }
        }
    }, [searchParams]);

    // Sync selectedTherapistId with activeTherapistId prop
    useEffect(() => {
        if (activeTherapistId) {
            setSelectedTherapistId(activeTherapistId);
        } else {
            setSelectedTherapistId(null);
        }
    }, [activeTherapistId]);

    // If the selected therapist is archived (or just got archived), ensure archived section is visible
    useEffect(() => {
        if (suppressForceShowArchivedRef.current) return;
        if (forceShowArchived && !showArchived) {
            setShowArchived(true);

            // Scroll to bottom when showing archived therapists (match behavior of handleShowArchived)
            setTimeout(() => {
                if (therapistListRef.current) {
                    therapistListRef.current.scrollTo({
                        top: therapistListRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 100);
        }
    }, [forceShowArchived, showArchived]);

    // Re-enable forceShowArchived behavior once selection is no longer archived
    useEffect(() => {
        if (!forceShowArchived) {
            suppressForceShowArchivedRef.current = false;
        }
    }, [forceShowArchived]);

    // Keep the selected therapist scrolled into view (important after archive/unarchive moves them between sections)
    useEffect(() => {
        if (!selectedTherapistId || !therapistListRef.current) return;

        const container = therapistListRef.current;
        const id = String(selectedTherapistId);

        const t = setTimeout(() => {
            // If user just hid archived list manually, do not auto-scroll as a side effect
            if (skipNextAutoScrollRef.current) {
                skipNextAutoScrollRef.current = false;
                return;
            }

            const el = container.querySelector(`[data-therapist-id="${id}"]`);
            if (el && typeof el.scrollIntoView === 'function') {
                el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            }
        }, 0);

        return () => clearTimeout(t);
    }, [
        selectedTherapistId,
        showArchived,
        filteredTherapists?.length,
        filteredArchivedTherapists?.length
    ]);

    // Fetch archived therapists when showArchived is toggled
    useEffect(() => {
        const fetchArchivedTherapists = async () => {
            // Don't fetch if archived is not shown
            if (!showArchived) {
                return;
            }
            
            // Wait for user assigned outlets to be loaded first
            if (!userAssignedOutlets || userAssignedOutlets.length === 0) {
                return;
            }
            
            // Don't fetch if outlet is not selected
            if (!selectedOutlet) {
                return;
            }
            
            // Validate that the selected outlet exists in user's assigned outlets
            const assignedOutlet = userAssignedOutlets.find(outlet => String(outlet.id) === String(selectedOutlet));
            if (!assignedOutlet) {
                setArchivedTherapists([]);
                setFilteredArchivedTherapists([]);
                return;
            }
            
            setIsLoadingArchived(true);
            try {
                const response = await getTherapistList({
                    outlet: selectedOutlet,
                    pagination: 0,
                    status: 0, // 0 for archived
                });

                if (response?.data) {
                    const staffList = response.data?.data?.data?.list?.staffs || [];
                    // Filter to get only therapists (not receptionists)
                    const therapistsOnly = staffList.filter((staff) => {
                        const role = (staff.role || '').toString().toLowerCase();
                        const isReception = role.includes('receptionist');
                        const isStaff = role ? role.includes('staff') : true;
                        return !isReception && isStaff;
                    });
                    
                    setArchivedTherapists(therapistsOnly);
                    setFilteredArchivedTherapists(therapistsOnly);
                }
            } catch (error) {
                console.error('Error fetching archived therapists:', error);
                setArchivedTherapists([]);
                setFilteredArchivedTherapists([]);
            } finally {
                setIsLoadingArchived(false);
            }
        };

        fetchArchivedTherapists();
    }, [selectedOutlet, showArchived, refreshTrigger, userAssignedOutlets]);

    // Socket integration for real-time updates
    useEffect(() => {
        if (!selectedOutlet) {
            return;
        }

        // Subscribe to therapist update events for this outlet
        const channel = Echo.channel(`cache-sync.outlet.${selectedOutlet}.therapist`);
        const outletChannel = Echo.channel("outlet");
        const attendanceChannel = Echo.channel("attendance");
        
        channel.listen('.data.updated', (event) => {
            // Data has been updated on the server, show indicator
            setHasDataUpdate(true);
        });

        outletChannel.listen(".therapist.schedule.updated", (event) => {
            setHasDataUpdate(true);
        });

        // Listen for attendance updates
        attendanceChannel.listen("attendance.updated", (event) => {
           setHasDataUpdate(true);
        });
        attendanceChannel.listen(".attendance.updated", (event) => {
           setHasDataUpdate(true);
        });

        // Cleanup on unmount or outlet change
        return () => {
            channel.stopListening('.data.updated');
            outletChannel.stopListening(".therapist.schedule.updated");
            attendanceChannel.stopListening("attendance.updated");
            attendanceChannel.stopListening(".attendance.updated");

            Echo.leave(`cache-sync.outlet.${selectedOutlet}.therapist`);
            Echo.leave("outlet");
            Echo.leave("attendance");
        };
    }, [selectedOutlet]);

    // Filter therapists based on search query
    useEffect(() => {
        if (!searchQuery.trim()) {
            setFilteredTherapists(therapists);
            setFilteredArchivedTherapists(archivedTherapists);
        } else {
            const search = searchQuery.toLowerCase();
            
            // Filter active therapists
            const filteredActive = therapists.filter((therapist) => {
                const name = (therapist.alias || therapist.name || '').toLowerCase();
                return name.includes(search);
            }).sort((a, b) => {
                // Sort: names starting with search query come first
                const nameA = (a.alias || a.name || '').toLowerCase();
                const nameB = (b.alias || b.name || '').toLowerCase();
                const startsWithA = nameA.startsWith(search);
                const startsWithB = nameB.startsWith(search);
                
                if (startsWithA && !startsWithB) return -1;
                if (!startsWithA && startsWithB) return 1;
                return 0;
            });
            setFilteredTherapists(filteredActive);
            
            // Filter archived therapists
            const filteredArchived = archivedTherapists.filter((therapist) => {
                const name = (therapist.alias || therapist.name || '').toLowerCase();
                return name.includes(search);
            }).sort((a, b) => {
                // Sort: names starting with search query come first
                const nameA = (a.alias || a.name || '').toLowerCase();
                const nameB = (b.alias || b.name || '').toLowerCase();
                const startsWithA = nameA.startsWith(search);
                const startsWithB = nameB.startsWith(search);
                
                if (startsWithA && !startsWithB) return -1;
                if (!startsWithA && startsWithB) return 1;
                return 0;
            });
            setFilteredArchivedTherapists(filteredArchived);
        }
    }, [searchQuery, therapists, archivedTherapists]);

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
    };

    const handleShowArchived = () => {
        const newShowArchived = !showArchived;

        // Prevent auto-scroll when user clicks "Hide Archived Therapist"
        if (showArchived && !newShowArchived) {
            skipNextAutoScrollRef.current = true;
        }

        if (showArchived && !newShowArchived && forceShowArchived) {
            suppressForceShowArchivedRef.current = true;
            setSelectedTherapistId(null);
            if (onClearSelection) onClearSelection();
        }

        setShowArchived(newShowArchived);
        
        // Scroll to bottom when showing archived therapists
        if (newShowArchived) {
            setTimeout(() => {
                if (therapistListRef.current) {
                    therapistListRef.current.scrollTo({
                        top: therapistListRef.current.scrollHeight,
                        behavior: 'smooth'
                    });
                }
            }, 100); // Small delay to ensure content is rendered
        }
    };

    const handleRefresh = () => {
        // Clear ETag to force full fetch
        const etagKey = getETagKey(selectedOutlet, 1);
        localStorage.removeItem(etagKey);
        // Clear global cache for this outlet
        if (globalTherapistCache[selectedOutlet]) {
            delete globalTherapistCache[selectedOutlet];
        }
        
        setHasDataUpdate(false);
        setLocalRefreshTrigger(prev => prev + 1);
        // Reset throttle to allow immediate refresh
        lastFetchTimeRef.current = 0;
    };

    const getGenderColor = (gender) => {
        return gender?.toLowerCase() === 'female' ? 'female' : 'male';
    };

    // Function to get status indicator class based on check-in/out status
    const getStatusIndicator = (therapist) => {
        const hasCheckedIn = therapist?.daily_checkin !== null && therapist?.daily_checkin !== undefined;
        const hasCheckedOut = therapist?.daily_checkout !== null && therapist?.daily_checkout !== undefined;
        
        if (hasCheckedIn && hasCheckedOut) {
            return 'checkedOut'; // Red dot - checked out
        } else if (hasCheckedIn) {
            return 'checkedIn'; // Green dot - checked in
        }
        return null; // No dot
    };

    const handleTherapistClick = (therapist) => {
        // Don't call onSelectTherapist if the same therapist is already selected
        if (selectedTherapistId === therapist.id) {
            return;
        }
        
        setSelectedTherapistId(therapist.id);
        if (onSelectTherapist) {
            onSelectTherapist(therapist);
        }
    };

    const reorderListByIds = (list, sourceId, targetId, position = 'before') => {
        if (!Array.isArray(list) || !sourceId) return list;
        const updated = [...list];
        const fromIndex = updated.findIndex((item) => item.id === sourceId);
        if (fromIndex === -1) return list;

        const [movedItem] = updated.splice(fromIndex, 1);

        if (!targetId) {
            updated.push(movedItem);
            return updated;
        }

        const toIndex = updated.findIndex((item) => item.id === targetId);
        if (toIndex === -1) {
            updated.push(movedItem);
            return updated;
        }

        const insertIndex = position === 'after' ? toIndex + 1 : toIndex;
        updated.splice(insertIndex, 0, movedItem);
        return updated;
    };

    const applyReorder = (sourceId, targetId, position = 'before') => {
        if (!sourceId) return;
        setTherapists((prev) => {
            const next = reorderListByIds(prev, sourceId, targetId, position);
            therapistsOrderRef.current = next;
            return next;
        });
        setFilteredTherapists((prev) => reorderListByIds(prev, sourceId, targetId, position));
    };

    const syncHomeStaffListOrder = (reorderedList) => {
        if (!setHomeStaffList || !Array.isArray(reorderedList) || reorderedList.length === 0) {
            return;
        }

        const orderMap = new Map(
            reorderedList.map((therapist, index) => [String(therapist.id), index])
        );

        setHomeStaffList((prev) => {
            if (!Array.isArray(prev) || prev.length === 0) {
                return prev;
            }

            return [...prev].sort((a, b) => {
                const orderA = orderMap.get(String(a.id));
                const orderB = orderMap.get(String(b.id));
                const indexA = orderA !== undefined ? orderA : Number.MAX_SAFE_INTEGER;
                const indexB = orderB !== undefined ? orderB : Number.MAX_SAFE_INTEGER;
                return indexA - indexB;
            });
        });
    };

    const saveSortOrder = async (reorderedList) => {
        try {
            // Create comma-separated list of therapist IDs in the new order
            const order = reorderedList.map(therapist => therapist.id).join(',');
            
            const payload = {
                order: order,
                receptionist_id: loggedUser || null,
                outlet_id: selectedOutlet || null
            };

            const response = await saveTherapistSortOrder(payload);
            
            if (response?.data?.success || response?.success) {
                syncHomeStaffListOrder(reorderedList);
            } else {
                console.error('Failed to save therapist sort order:', response?.data?.message || response?.message);
            }
        } catch (error) {
            console.error('Error saving therapist sort order:', error);
        }
    };

    const handleDragStart = (event, therapistId) => {
        event.preventDefault();
        event.stopPropagation();
        
        isDraggingRef.current = true;
        hasMovedRef.current = false; // Reset on drag start
        therapistsOrderRef.current = therapists;
        setDraggedTherapistId(therapistId);
        setDropTargetTherapistId(therapistId);
        setDropIndicator({ id: therapistId, position: 'before' });

        const sourceNode = event.currentTarget;
        const sourceRect = sourceNode.getBoundingClientRect();
        
        dragStartPos.current = {
            x: event.clientX - sourceRect.left,
            y: event.clientY - sourceRect.top
        };

        // Create custom drag preview
        setCustomDragPreview({
            therapistId,
            x: event.clientX,
            y: event.clientY,
            width: sourceRect.width,
            height: sourceRect.height
        });
    };

    const handleMouseMove = (event) => {
        if (!isDraggingRef.current || !customDragPreview) return;

        setCustomDragPreview(prev => ({
            ...prev,
            x: event.clientX,
            y: event.clientY
        }));

        // Find element under cursor
        const elementsUnderCursor = document.elementsFromPoint(event.clientX, event.clientY);
        const therapistElement = elementsUnderCursor.find(el => 
            el.classList.contains(styles.therapistItem) && el.draggable
        );

        if (therapistElement) {
            const targetId = parseInt(therapistElement.getAttribute('data-therapist-id'));
            if (targetId && targetId !== draggedTherapistId) {
                const rect = therapistElement.getBoundingClientRect();
                const offsetY = event.clientY - rect.top;
                const position = offsetY > rect.height / 2 ? 'after' : 'before';

                if (dropIndicator.id !== targetId || dropIndicator.position !== position) {
                    hasMovedRef.current = true; // Mark that a drag operation occurred
                    setDropTargetTherapistId(targetId);
                    setDropIndicator({ id: targetId, position });
                    applyReorder(draggedTherapistId, targetId, position);
                }
            }
        }
    };

    const handleMouseUp = (event) => {
        if (!isDraggingRef.current) return;
        
        const hadMoved = hasMovedRef.current;
        
        isDraggingRef.current = false;
        hasMovedRef.current = false;
        setCustomDragPreview(null);
        setDraggedTherapistId(null);
        setDropTargetTherapistId(null);
        setDropIndicator({ id: null, position: 'before' });

        // Only save the sort order if the therapist was actually dragged and repositioned
        const reorderedList = therapistsOrderRef.current;
        if (hadMoved && reorderedList.length > 0) {
            saveSortOrder(reorderedList);
        }
    };

    // Add global mouse listeners for custom drag
    useEffect(() => {
        if (isDraggingRef.current) {
            document.addEventListener('mousemove', handleMouseMove, { passive: false });
            document.addEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = 'grabbing';
            document.body.style.userSelect = 'none';
        }

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
        };
    }, [isDraggingRef.current, customDragPreview, draggedTherapistId, dropIndicator]);

    const handleDragOver = (event, targetId) => {
        event.preventDefault();
        event.stopPropagation();
        if (!draggedTherapistId || draggedTherapistId === targetId) {
            return;
        }

        const rect = event.currentTarget.getBoundingClientRect();
        const offsetY = event.clientY - rect.top;
        const position = offsetY > rect.height / 2 ? 'after' : 'before';

        if (
            dropIndicator.id === targetId &&
            dropIndicator.position === position
        ) {
            return;
        }

        setDropTargetTherapistId(targetId);
        setDropIndicator({ id: targetId, position });
        applyReorder(draggedTherapistId, targetId, position);
    };

    const handleDropOnItem = (event) => {
        event.preventDefault();
        event.stopPropagation();
        handleDragEnd();
    };

    const handleListDragOver = (event) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = 'move';
    };

    const handleListDropToEnd = (event) => {
        event.preventDefault();
        if (!draggedTherapistId) return;
        applyReorder(draggedTherapistId, null, 'after');
        handleDragEnd();
    };

    const handleDragEnd = () => {
        setDraggedTherapistId(null);
        setDropTargetTherapistId(null);
        setDropIndicator({ id: null, position: 'before' });
        if (dragImageRef.current) {
            if (document.body.contains(dragImageRef.current)) {
                document.body.removeChild(dragImageRef.current);
            }
            dragImageRef.current = null;
        }
    };

    // Function to highlight matching characters in the name
    const highlightMatch = (text, query) => {
        if (!query || !text) return text;
        
        // Convert to string and trim whitespace
        const textStr = String(text).trim();
        const searchStr = String(query).trim();
        
        if (!textStr || !searchStr) return textStr;
        
        // Escape special regex characters
        const escapedSearchTerm = searchStr.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
        
        // Check if there's a match
        if (!regex.test(textStr)) {
            return textStr;
        }
        
        // Split text by matches and rebuild with highlighting
        const parts = textStr.split(regex);
        
        return (
            <span>
                {parts.map((part, index) => {
                    // Check if this part matches the search term (case insensitive)
                    if (part && new RegExp(`^${escapedSearchTerm}$`, 'gi').test(part)) {
                        return (
                            <strong key={`highlight-${index}`} style={{ fontWeight: 'bold' }}>
                                {part}
                            </strong>
                        );
                    }
                    return part || '';
                }).filter(part => part !== '')}
            </span>
        );
    };


    return (
        <div className={styles.therapistListContainer}>
            {/* Search Bar */}
            <div className={styles.searchContainer}>
                <div className={styles.searchBar}>
                    <SearchIcon size={16} />
                    <input
                        type="text"
                        placeholder="Search therapist name"
                        value={searchQuery}
                        onChange={handleSearchChange}
                        className={styles.searchInput}
                    />
                </div>
            </div>

            {/* Filter Label */}
            <div className={styles.filterContainer}>
                <label className={styles.filterLabel}>All Therapist</label>
                <button
                    type="button"
                    onClick={handleRefresh}
                    className={`${styles.refreshButton} ${isLoading ? styles.isRefreshing : ''}`}
                    title="Refresh therapist list"
                >
                    <RepeatBookingIcon size={16} />
                    {hasDataUpdate && !isLoading && <span className={styles.updateIndicator}></span>}
                </button>
            </div>

            {/* Therapist List */}
            <div className={styles.therapistList} ref={therapistListRef}>
                {isLoading && filteredTherapists.length === 0 ? (
                    <div className={styles.loading}>Loading therapists...</div>
                ) : filteredTherapists.length > 0 ? (
                    <div
                        className={styles.draggableList}
                        onDragOver={handleListDragOver}
                        onDrop={handleListDropToEnd}
                    >
                        {filteredTherapists.map((therapist) => {
                        const statusClass = getStatusIndicator(therapist);
                        return (
                            <div 
                                key={therapist.id} 
                                data-therapist-id={therapist.id}
                                className={[
                                    styles.therapistItem,
                                    selectedTherapistId === therapist.id ? styles.selected : '',
                                    draggedTherapistId === therapist.id ? styles.draggingItem : '',
                                    dropTargetTherapistId === therapist.id && draggedTherapistId !== therapist.id ? styles.dropTarget : '',
                                    dropIndicator.id === therapist.id && dropIndicator.position === 'before' && draggedTherapistId !== therapist.id ? styles.dropBefore : '',
                                    dropIndicator.id === therapist.id && dropIndicator.position === 'after' && draggedTherapistId !== therapist.id ? styles.dropAfter : ''
                                ].filter(Boolean).join(' ')}
                                onClick={() => handleTherapistClick(therapist)}
                                draggable
                                onMouseDown={(event) => handleDragStart(event, therapist.id)}
                            >
                                <div 
                                    className={`${styles.therapistBadge} ${styles[getGenderColor(therapist.gender)]}`}
                                >
                                    {therapist.pager_number || "N/A"}
                                </div>
                                <span className={styles.therapistName}>
                                    {highlightMatch(therapist.alias || therapist.name, searchQuery)}
                                    {therapist.is_part_time && <span className={styles.asterisk}>*</span>}
                                    {statusClass && (
                                        <span className={`${styles.statusIndicator} ${styles[statusClass]}`}></span>
                                    )}
                                </span>
                            </div>
                        );
                        })}
                    </div>
                ) : (
                    <div className={styles.noResults}>
                        {searchQuery ? 'No therapists found.' : 'No therapists available.'}
                    </div>
                )}

                {/* Show Archived Link */}
                <div className={styles.archivedLink}>
                    <button 
                        type="button"
                        onClick={handleShowArchived}
                        className={styles.archivedButton}
                    >
                        {showArchived ? 'Hide Archived Therapist' : 'Show Archived Therapist'}
                    </button>
                </div>

                {/* Archived Therapists - shown in same scroll container */}
                {showArchived && (
                    <>
                        <div className={styles.archivedDivider}>
                            <label className={styles.archivedLabel}>Archived Therapists</label>
                        </div>
                        {isLoadingArchived ? (
                            <div className={styles.loading}>Loading archived therapists...</div>
                        ) : filteredArchivedTherapists.length > 0 ? (
                            filteredArchivedTherapists.map((therapist) => {
                                const statusClass = getStatusIndicator(therapist);
                                return (
                                    <div 
                                        key={therapist.id} 
                                        data-therapist-id={therapist.id}
                                        className={`${styles.therapistItem} ${selectedTherapistId === therapist.id ? styles.selected : ''}`}
                                        onClick={() => handleTherapistClick(therapist)}
                                    >
                                        <div 
                                            className={`${styles.therapistBadge} ${styles[getGenderColor(therapist.gender)]}`}
                                        >
                                            {therapist.pager_number || "N/A"}
                                        </div>
                                        <span className={styles.therapistName}>
                                            {highlightMatch(therapist.alias || therapist.name, searchQuery)}
                                            {therapist.is_part_time && <span className={styles.asterisk}>*</span>}
                                            {statusClass && (
                                                <span className={`${styles.statusIndicator} ${styles[statusClass]}`}></span>
                                            )}
                                        </span>
                                    </div>
                                );
                            })
                        ) : (
                            <div className={styles.noResults}>
                                {searchQuery ? 'No archived therapists found.' : 'No archived therapists available.'}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Custom Drag Preview */}
            {customDragPreview && (() => {
                const draggedTherapist = filteredTherapists.find(t => t.id === customDragPreview.therapistId);
                if (!draggedTherapist) return null;
                const statusClass = getStatusIndicator(draggedTherapist);
                
                return (
                    <div 
                        className={styles.customDragPreview}
                        style={{
                            position: 'fixed',
                            left: customDragPreview.x - dragStartPos.current.x,
                            top: customDragPreview.y - dragStartPos.current.y,
                            width: customDragPreview.width,
                            height: customDragPreview.height,
                            pointerEvents: 'none',
                            zIndex: 9999,
                            opacity: 1
                        }}
                    >
                        <div className={styles.therapistItem} style={{ margin: 0 }}>
                            <div className={`${styles.therapistBadge} ${styles[getGenderColor(draggedTherapist.gender)]}`}>
                                {draggedTherapist.pager_number || "N/A"}
                            </div>
                            <span className={styles.therapistName}>
                                {draggedTherapist.alias || draggedTherapist.name}
                                {draggedTherapist.is_part_time && <span className={styles.asterisk}>*</span>}
                                {statusClass && (
                                    <span className={`${styles.statusIndicator} ${styles[statusClass]}`}></span>
                                )}
                            </span>
                        </div>
                    </div>
                );
            })()}
        </div>
    );
};

export default TherapistList;
